/**
 * Lineup Wheel Simulator — pick a random PL XI through wheel draws,
 * then Monte Carlo-simulate the season.
 *
 * Modes:
 *  - Standard: full PL pool
 *  - Fantasy: top-200 most fantasy-relevant players
 *
 * Difficulty:
 *  - Blind:    only price + position visible on each wheel candidate
 *  - Standard: name + team + ownership + ppg
 *  - Pro:      adds total points + form + minutes
 */
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
    SoccerBall, Lightning, ArrowsClockwise, Crown, Spinner, X, Plus,
    Trophy, Eye, EyeClosed, Brain, FloppyDisk, Sparkle, ArrowRight,
    Share, TwitterLogo, WhatsappLogo, Swap, ShareNetwork,
} from "@phosphor-icons/react";
import AppShell from "../components/AppShell";
import { track } from "../lib/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// FPL XI formations. Most-popular highlighted (3-4-3, 3-5-2) per user request.
const FORMATIONS = {
    "3-4-3": { GKP: 1, DEF: 3, MID: 4, FWD: 3, popular: true },
    "3-5-2": { GKP: 1, DEF: 3, MID: 5, FWD: 2, popular: true },
    "4-3-3": { GKP: 1, DEF: 4, MID: 3, FWD: 3 },
    "4-4-2": { GKP: 1, DEF: 4, MID: 4, FWD: 2 },
    "4-5-1": { GKP: 1, DEF: 4, MID: 5, FWD: 1 },
    "5-3-2": { GKP: 1, DEF: 5, MID: 3, FWD: 2 },
    "5-4-1": { GKP: 1, DEF: 5, MID: 4, FWD: 1 },
};

// Fixed FPL 15-man squad composition — independent of any formation.
// Slot indices: 0-1=GKP, 2-6=DEF, 7-11=MID, 12-14=FWD.
const SLOT_DEFS = (() => {
    const out = [];
    let idx = 0;
    for (const [pos, n] of [["GKP", 2], ["DEF", 5], ["MID", 5], ["FWD", 3]]) {
        for (let i = 0; i < n; i++) {
            out.push({ idx, pos });
            idx += 1;
        }
    }
    return out;
})();
const DEFAULT_STARTER_IDXS = [0, 2, 3, 4, 5, 7, 8, 9, 10, 12, 13];  // 4-4-2
const FORMATION_LIMITS = {  // legal XI composition per FPL rules
    GKP: [1, 1],
    DEF: [3, 5],
    MID: [2, 5],
    FWD: [1, 3],
};

// Build the ordered list of (idx,pos) slot descriptors for a formation.
function buildSlots(formation) {
    const order = ["GKP", "DEF", "MID", "FWD"];
    const out = [];
    let idx = 0;
    for (const pos of order) {
        for (let i = 0; i < (formation[pos] || 0); i++) {
            out.push({ idx, pos });
            idx += 1;
        }
    }
    return out;
}

const POS_COLOR = {
    GKP: { text: "text-fa-cream", border: "border-fa-cream", bg: "bg-fa-cream/10" },
    DEF: { text: "text-fa-mint",  border: "border-fa-mint",  bg: "bg-fa-mint/10" },
    MID: { text: "text-fa-sky",   border: "border-fa-sky",   bg: "bg-fa-sky/10" },
    FWD: { text: "text-fa-yellow",border: "border-fa-yellow",bg: "bg-fa-yellow/10" },
};

const DIFFICULTY_INFO = {
    blind:    { label: "Blind",    icon: EyeClosed, hint: "Only price + position visible — pure gut feeling." },
    standard: { label: "Standard", icon: Eye,       hint: "Name + team + ownership + points-per-game." },
    pro:      { label: "Pro",      icon: Brain,     hint: "Full stats — total points, form, minutes." },
};

function PlayerCard({ player, isCaptain, slotIdx, isDragOver, fdrPreview,
                     onSetCaptain, onRemove, onOpenSwap,
                     onDragStart, onDragOver, onDragLeave, onDrop }) {
    if (!player) return null;
    const c = POS_COLOR[player.position] || { text: "text-white/70", border: "border-white/30", bg: "bg-white/[0.04]" };
    const dragRing = isDragOver ? "ring-2 ring-fa-yellow" : "";
    // FDR badge colour bands (1=easy/green, 5=hard/red)
    const fdrColor = (fdr) => {
        if (fdr == null) return "bg-white/10 text-white/40";
        if (fdr < 2.3) return "bg-green-500/30 text-green-200";
        if (fdr < 3.0) return "bg-fa-mint/30 text-fa-mint";
        if (fdr < 3.6) return "bg-fa-yellow/30 text-fa-yellow";
        if (fdr < 4.3) return "bg-orange-500/30 text-orange-200";
        return "bg-red-500/30 text-red-200";
    };
    return (
        <div className={`border ${c.border} ${c.bg} ${dragRing} px-2 py-1.5 relative group cursor-grab active:cursor-grabbing`}
             draggable={true}
             onDragStart={(e) => { e.dataTransfer.effectAllowed = "move"; e.dataTransfer.setData("text/plain", String(slotIdx)); onDragStart?.(slotIdx); }}
             onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; onDragOver?.(slotIdx); }}
             onDragLeave={() => onDragLeave?.(slotIdx)}
             onDrop={(e) => { e.preventDefault(); onDrop?.(slotIdx); }}
             data-testid={`slot-filled-${player.position}-${player.id}`}>
            <div className="flex items-center justify-between gap-1">
                <span className="font-display font-black text-[13px] truncate">
                    {player.name}
                </span>
                {isCaptain && (
                    <span className="font-mono text-[8px] font-bold tracking-widest text-black bg-fa-yellow px-1">
                        C
                    </span>
                )}
            </div>
            <div className="font-mono text-[9px] uppercase tracking-widest text-white/50 flex items-center justify-between">
                <span>{player.team_name} · {player.position}</span>
                <span className="text-white/70 tabular-nums">£{player.price}</span>
            </div>
            {fdrPreview && fdrPreview.avg != null && (
                <div className={`mt-1 inline-flex items-center gap-1.5 px-1.5 py-0.5 font-mono text-[8px] uppercase tracking-widest tabular-nums ${fdrColor(fdrPreview.avg)}`}
                     title={fdrPreview.title}
                     data-testid={`fdr-badge-${player.id}`}>
                    <span>FDR {fdrPreview.avg.toFixed(1)}</span>
                    {fdrPreview.next_opp && (
                        <span className="text-white/70 normal-case">· {fdrPreview.next_opp}</span>
                    )}
                </div>
            )}
            <div className="absolute inset-0 flex items-center justify-center gap-1 opacity-0 group-hover:opacity-100 bg-black/70 transition-opacity">
                <button type="button" onClick={onSetCaptain}
                        data-testid={`set-captain-${player.id}`}
                        title={isCaptain ? "Captain" : "Set captain"}
                        className={`font-mono text-[9px] uppercase tracking-widest px-1.5 py-0.5 border ${isCaptain ? "bg-fa-yellow text-black border-fa-yellow" : "border-white/40 text-white hover:bg-white/10"}`}>
                    <Crown size={9} weight={isCaptain ? "fill" : "regular"} className="inline mb-0.5 mr-0.5" />
                    {isCaptain ? "C" : "MAKE C"}
                </button>
                {onOpenSwap && (
                    <button type="button" onClick={(e) => { e.stopPropagation(); onOpenSwap(slotIdx); }}
                            data-testid={`swap-btn-${player.id}`}
                            title="Swap with another player in your squad"
                            className="font-mono text-[9px] uppercase tracking-widest px-1.5 py-0.5 border border-fa-yellow/60 text-fa-yellow hover:bg-fa-yellow/10">
                        <Swap size={9} weight="bold" className="inline mb-0.5 mr-0.5" /> SWAP
                    </button>
                )}
                <button type="button" onClick={(e) => { e.stopPropagation(); onRemove?.(); }}
                        data-testid={`remove-${player.id}`}
                        title="Remove from squad"
                        className="font-mono text-[9px] uppercase tracking-widest px-1.5 py-0.5 border border-red-300/60 text-red-300 hover:bg-red-300/10">
                    <X size={9} weight="bold" className="inline mb-0.5 mr-0.5" /> X
                </button>
            </div>
        </div>
    );
}

function EmptySlot({ pos, slotIdx, isDragOver, onSpin,
                    onDragOver, onDragLeave, onDrop }) {
    const c = POS_COLOR[pos] || { text: "text-white/60", border: "border-white/30", bg: "bg-white/[0.03]" };
    const dragRing = isDragOver ? "ring-2 ring-fa-yellow" : "";
    return (
        <button type="button" onClick={onSpin}
                data-testid={`slot-empty-${pos}`}
                onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; onDragOver?.(slotIdx); }}
                onDragLeave={() => onDragLeave?.(slotIdx)}
                onDrop={(e) => { e.preventDefault(); onDrop?.(slotIdx); }}
                className={`border-2 border-dashed ${c.border} ${c.bg} ${dragRing} hover:bg-opacity-30 px-2 py-1.5 flex flex-col items-center justify-center gap-0.5 transition-all`}>
            <ArrowsClockwise size={14} weight="bold" className={`${c.text} animate-pulse`} />
            <span className={`font-mono text-[9px] uppercase tracking-widest font-bold ${c.text}`}>
                Spin {pos}
            </span>
        </button>
    );
}

function SwapPickerModal({ open, fromIdx, allSlots, squad, starterIdxs,
                          onPick, onClose }) {
    if (!open || fromIdx == null) return null;
    const fromSlot = allSlots.find((s) => s.idx === fromIdx);
    const fromPlayer = squad[fromIdx];
    if (!fromSlot || !fromPlayer) return null;
    const isFromStarter = starterIdxs.has(fromIdx);
    // Eligible targets:
    //   (a) Cross-group (bench↔starter) of ANY position — drag-to-flip role,
    //       formation legality is checked on swap.
    //   (b) Same-group reorder — only same position.
    const candidates = allSlots.filter((s) => {
        if (s.idx === fromIdx) return false;
        const isTargetStarter = starterIdxs.has(s.idx);
        if (isTargetStarter === isFromStarter) {
            // Same group: must be same position
            return s.pos === fromSlot.pos;
        }
        // Cross group: any position OK (validity checked on actual swap)
        return true;
    });
    // Sort: opposite-group first (the more interesting "promote/demote" swaps),
    // then filled before empty, then by position GKP→FWD
    const POS_ORDER = { GKP: 0, DEF: 1, MID: 2, FWD: 3 };
    candidates.sort((a, b) => {
        const aOpp = starterIdxs.has(a.idx) !== isFromStarter ? 0 : 1;
        const bOpp = starterIdxs.has(b.idx) !== isFromStarter ? 0 : 1;
        if (aOpp !== bOpp) return aOpp - bOpp;
        const aFilled = squad[a.idx] ? 0 : 1;
        const bFilled = squad[b.idx] ? 0 : 1;
        if (aFilled !== bFilled) return aFilled - bFilled;
        return POS_ORDER[a.pos] - POS_ORDER[b.pos];
    });
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
             onClick={onClose} data-testid="swap-picker-modal">
            <div className="border-2 border-fa-yellow bg-[#0d0d0d] max-w-lg w-full p-4 space-y-3"
                 onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between">
                    <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-fa-yellow flex items-center gap-2">
                        <Swap size={12} weight="fill" /> Swap {fromPlayer.name}
                    </div>
                    <button type="button" onClick={onClose} data-testid="swap-picker-close"
                            className="font-mono text-[10px] text-white/40 hover:text-white">
                        <X size={14} weight="bold" />
                    </button>
                </div>
                <div className="font-mono text-[9px] uppercase tracking-widest text-white/45">
                    {isFromStarter ? "Pick a bench player to demote (or another XI slot to reorder)" : "Pick a starter to promote into the XI"}
                </div>
                {candidates.length === 0 ? (
                    <div className="font-mono text-[10px] text-white/50 italic py-6 text-center">
                        No legal swap targets — position rules prevent it.
                    </div>
                ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-[60vh] overflow-y-auto">
                        {candidates.map((s) => {
                            const p = squad[s.idx];
                            const groupTag = starterIdxs.has(s.idx) ? "XI" : "BENCH";
                            const slotPosLabel = s.pos;
                            return (
                                <button key={s.idx} type="button"
                                        onClick={() => onPick(s.idx)}
                                        data-testid={`swap-target-${s.idx}`}
                                        className="border border-white/15 bg-white/[0.02] hover:border-fa-yellow hover:bg-fa-yellow/5 p-2 text-left transition-all">
                                    <div className="flex items-center justify-between mb-0.5">
                                        <span className="font-display font-black text-sm truncate">
                                            {p ? p.name : `Empty ${slotPosLabel}`}
                                        </span>
                                        <span className="font-mono text-[8px] tracking-widest text-fa-yellow bg-fa-yellow/10 px-1">{groupTag}·{slotPosLabel}</span>
                                    </div>
                                    {p ? (
                                        <div className="font-mono text-[9px] uppercase tracking-widest text-white/50">
                                            {p.team_name} · {p.position} · £{p.price}
                                        </div>
                                    ) : (
                                        <div className="font-mono text-[9px] uppercase tracking-widest text-white/35 italic">
                                            move here (slot is empty)
                                        </div>
                                    )}
                                </button>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
}

const SET_PIECE_META = {
    penalty_taker:    { label: "Penalty",   short: "PEN",  hint: "+0.18 goals/90" },
    freekick_taker:   { label: "Free-kick", short: "FK",   hint: "+0.04 goals · +0.06 assists/90" },
    corner_taker:     { label: "Corners",   short: "COR",  hint: "+0.12 assists/90" },
    long_throw_taker: { label: "Long throw",short: "LT",   hint: "+0.04 assists/90" },
};

function SetPiecesPanel({ squad, setPieces, onChange, enabled }) {
    // squad is { slotIdx: player } — flatten to list of {id, name, position, team_name}
    const players = Object.values(squad).filter(Boolean);
    const playersById = Object.fromEntries(players.map((p) => [p.id, p]));
    return (
        <div className={`border ${enabled ? "border-fa-yellow/30" : "border-white/10"} bg-[#0d0d0d] p-3 space-y-2`}
             data-testid="set-pieces-panel">
            <div className="flex items-center justify-between">
                <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-fa-yellow flex items-center gap-1.5">
                    <Crown size={11} weight="fill" />
                    Set-piece roles
                </div>
                <span className="font-mono text-[9px] uppercase tracking-widest text-white/40">
                    {enabled ? "Assigns boosts to the simulator" : `Fill ${11 - players.length} more slot${11 - players.length === 1 ? "" : "s"} first`}
                </span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
                {Object.entries(SET_PIECE_META).map(([role, meta]) => {
                    const selectedId = setPieces[role];
                    const selectedPlayer = selectedId ? playersById[selectedId] : null;
                    return (
                        <div key={role} className="border border-white/10 bg-black/30 p-2 space-y-1"
                             data-testid={`set-piece-${role}`}>
                            <div className="flex items-center justify-between">
                                <span className="font-display font-black text-sm uppercase">{meta.label}</span>
                                <span className="font-mono text-[8px] tracking-widest text-fa-yellow bg-fa-yellow/10 px-1 py-0.5">{meta.short}</span>
                            </div>
                            <p className="font-mono text-[9px] text-white/45">{meta.hint}</p>
                            <select
                                value={selectedId || ""}
                                onChange={(e) => onChange({ ...setPieces, [role]: e.target.value ? parseInt(e.target.value) : null })}
                                disabled={!enabled}
                                data-testid={`set-piece-select-${role}`}
                                className="w-full bg-black/50 border border-white/15 px-1.5 py-1 font-mono text-[10px] text-white focus:outline-none focus:border-fa-yellow disabled:opacity-50">
                                <option value="">— none —</option>
                                {players.map((p) => (
                                    <option key={p.id} value={p.id}>
                                        {p.name} ({p.position} · {p.team_name})
                                    </option>
                                ))}
                            </select>
                            {selectedPlayer && (
                                <div className="font-mono text-[9px] uppercase tracking-widest text-fa-mint truncate">
                                    {selectedPlayer.name} · {selectedPlayer.team_name}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

function PlayerSpinner({ roster, slot, filledIds, teamCounts, budgetLeft, maxPerClub }) {
    // Vertical roulette of eligible players for the current slot — respects
    // budget, team count, and excludes already-picked players. Falls back to a
    // generic spinner if the roster hasn't loaded yet.
    const eligible = useMemo(() => {
        if (!roster?.length) return [];
        const tc = teamCounts || {};
        return roster.filter((p) =>
            p.position === slot?.pos
            && !filledIds.includes(p.id)
            && (tc[p.team_id] || 0) < (maxPerClub || 3)
            && p.price <= (budgetLeft != null ? budgetLeft + 0.05 : 999)
        );
    }, [roster, slot, filledIds, teamCounts, budgetLeft, maxPerClub]);

    const [idx, setIdx] = useState(0);
    useEffect(() => {
        if (!eligible.length) return;
        let step = 55;
        let i = 0;
        let raf = null;
        const tick = () => {
            i += 1;
            // Jump by a random amount each tick so the reel "feels" chaotic, not linear
            setIdx((cur) => (cur + 1 + Math.floor(Math.random() * 5)) % eligible.length);
            step = Math.min(170, step * 1.05);
            raf = setTimeout(tick, step);
        };
        raf = setTimeout(tick, step);
        return () => clearTimeout(raf);
    }, [eligible]);

    if (!eligible.length) {
        return (
            <div className="font-mono text-[11px] text-white/60 flex items-center gap-2 py-8 justify-center"
                 data-testid="player-spinner-fallback">
                <Spinner size={14} weight="bold" className="animate-spin" />
                Spinning the wheel…
            </div>
        );
    }
    const visible = [
        eligible[(idx - 1 + eligible.length) % eligible.length],
        eligible[idx],
        eligible[(idx + 1) % eligible.length],
    ];
    return (
        <div className="py-5 space-y-2" data-testid="player-spinner">
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-fa-yellow text-center mb-2 flex items-center justify-center gap-2">
                <ArrowsClockwise size={11} weight="bold" className="animate-spin" />
                Spinning {eligible.length} eligible {slot?.pos}
            </div>
            <div className="relative h-[150px] overflow-hidden border-y-2 border-fa-yellow/40 bg-gradient-to-b from-transparent via-fa-yellow/5 to-transparent"
                 data-testid="player-spinner-reel">
                <div className="absolute inset-0 flex flex-col items-center justify-center px-3">
                    {/* Top blurred row */}
                    <div className="font-display font-black text-base text-white/15 truncate w-full text-center uppercase">
                        {visible[0]?.name} · {visible[0]?.team_name}
                    </div>
                    {/* Middle highlight row */}
                    <div className="font-display font-black text-2xl text-fa-yellow my-1 truncate w-full text-center uppercase"
                         data-testid="player-spinner-current">
                        {visible[1]?.name}
                    </div>
                    <div className="font-mono text-[10px] uppercase tracking-widest text-fa-yellow/80">
                        {visible[1]?.team_name} · £{visible[1]?.price}
                    </div>
                    {/* Bottom blurred row */}
                    <div className="font-display font-black text-base text-white/15 truncate w-full text-center uppercase mt-1">
                        {visible[2]?.name} · {visible[2]?.team_name}
                    </div>
                </div>
                <div className="absolute left-0 right-0 top-1/2 -translate-y-1/2 border-t border-b border-fa-yellow/60 h-14 pointer-events-none" />
            </div>
        </div>
    );
}

function WheelModal({ open, slot, candidates, difficulty, busy, roster, filledIds, teamCounts, budgetLeft, maxPerClub, onPick, onRespin, onClose }) {
    if (!open) return null;
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
             onClick={onClose}>
            <div className="border-2 border-fa-yellow bg-[#0d0d0d] max-w-2xl w-full p-4 space-y-3"
                 onClick={(e) => e.stopPropagation()}
                 data-testid="wheel-modal">
                <div className="flex items-center justify-between">
                    <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-fa-yellow flex items-center gap-2">
                        <ArrowsClockwise size={12} weight="fill" />
                        Wheel · pick your {slot?.pos}
                    </div>
                    <button type="button" onClick={onClose} data-testid="wheel-close"
                            className="font-mono text-[10px] text-white/40 hover:text-white">
                        <X size={14} weight="bold" />
                    </button>
                </div>
                {busy ? (
                    <PlayerSpinner roster={roster} slot={slot} filledIds={filledIds}
                                   teamCounts={teamCounts} budgetLeft={budgetLeft}
                                   maxPerClub={maxPerClub} />
                ) : (
                    <>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                            {candidates.map((c) => (
                                <button key={c.id} type="button" onClick={() => onPick(c)}
                                        data-testid={`wheel-pick-${c.id}`}
                                        className="border border-white/15 bg-white/[0.02] hover:border-fa-yellow hover:bg-fa-yellow/5 p-3 text-left transition-all">
                                    <div className="flex items-center justify-between mb-1">
                                        <span className="font-display font-black text-base truncate">
                                            {c.name}
                                        </span>
                                        <span className="font-mono text-[11px] tabular-nums text-fa-yellow font-bold">
                                            £{c.price}
                                        </span>
                                    </div>
                                    <div className="font-mono text-[10px] uppercase tracking-widest text-white/50">
                                        {c.team_name} · {c.position}
                                        {c.ownership != null && <span className="ml-2 text-white/40">{c.ownership.toFixed(1)}% own</span>}
                                    </div>
                                    {difficulty === "pro" && (
                                        <div className="font-mono text-[10px] text-white/55 mt-1 flex gap-2">
                                            <span>PPG {c.ppg?.toFixed(1)}</span>
                                            <span>Form {c.form?.toFixed(1)}</span>
                                            <span>{c.total_points}p</span>
                                        </div>
                                    )}
                                    {difficulty === "standard" && c.ppg != null && (
                                        <div className="font-mono text-[10px] text-white/55 mt-1">
                                            PPG {c.ppg.toFixed(1)}
                                        </div>
                                    )}
                                </button>
                            ))}
                        </div>
                        <div className="flex justify-between items-center pt-2 border-t border-white/10">
                            <p className="font-mono text-[10px] text-white/40 italic">
                                Each wheel respects your remaining budget + max-3-per-club rule.
                            </p>
                            <button type="button" onClick={onRespin} data-testid="wheel-respin"
                                    className="font-mono text-[10px] uppercase tracking-widest text-fa-yellow border border-fa-yellow/50 px-3 py-1.5 hover:bg-fa-yellow hover:text-black">
                                <ArrowsClockwise size={11} weight="bold" className="inline mr-1 mb-0.5" />
                                Re-spin
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}

// Free-pick modal: search + full roster list filtered by slot position, budget,
// max-3-per-club. Non-random, direct picking (for users who came from Timing
// Analyzer with a specific shortlist in mind).
function FreePickModal({ open, slot, roster, difficulty, filledIds, teamCounts,
                        budgetLeft, maxPerClub, onPick, onClose }) {
    const [q, setQ] = React.useState("");
    React.useEffect(() => { if (!open) setQ(""); }, [open]);
    if (!open) return null;
    const stripDia = (s) => (s || "").toString().normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "").toLowerCase();
    const query = stripDia(q.trim());
    const filledSet = new Set(filledIds);
    const eligible = roster
        .filter((p) => p.position === slot?.pos)
        .filter((p) => !filledSet.has(p.id))
        .filter((p) => p.price <= budgetLeft + 0.001)
        .filter((p) => (teamCounts[p.team_id] || 0) < maxPerClub)
        .filter((p) => !query
            || stripDia(p.name).includes(query)
            || stripDia(p.team_name).includes(query))
        .sort((a, b) => (b.ppg || 0) - (a.ppg || 0));

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
             onClick={onClose}>
            <div className="border-2 border-fa-mint bg-[#0d0d0d] max-w-3xl w-full p-4 space-y-3 max-h-[85vh] flex flex-col"
                 onClick={(e) => e.stopPropagation()}
                 data-testid="freepick-modal">
                <div className="flex items-center justify-between">
                    <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-fa-mint flex items-center gap-2">
                        <Plus size={12} weight="bold" />
                        Vyber si {slot?.pos} · rozpočet £{budgetLeft.toFixed(1)}m
                    </div>
                    <button type="button" onClick={onClose} data-testid="freepick-close"
                            className="font-mono text-[10px] text-white/40 hover:text-white">
                        <X size={14} weight="bold" />
                    </button>
                </div>
                <input
                    type="text"
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    autoFocus
                    placeholder="Vyhledej hráče nebo klub…"
                    data-testid="freepick-search"
                    className="w-full bg-black/40 border border-white/15 px-3 py-2 font-mono text-[12px] focus:outline-none focus:border-fa-mint"
                />
                <div className="flex-1 overflow-y-auto -mx-1 px-1">
                    {eligible.length === 0 ? (
                        <div className="font-mono text-[11px] text-white/40 italic p-6 text-center">
                            {query
                                ? `Žádný ${slot?.pos} nesplňuje "${q}" v rozpočtu £${budgetLeft.toFixed(1)}m.`
                                : `Žádný ${slot?.pos} nesplňuje rozpočet £${budgetLeft.toFixed(1)}m nebo max-${maxPerClub}-per-club.`}
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                            {eligible.map((p) => (
                                <button key={p.id} type="button" onClick={() => onPick(p)}
                                        data-testid={`freepick-add-${p.id}`}
                                        className="border border-white/10 bg-white/[0.02] hover:border-fa-mint hover:bg-fa-mint/5 p-2.5 text-left transition-all">
                                    <div className="flex items-center justify-between mb-0.5">
                                        <span className="font-display font-black text-[14px] truncate">
                                            {p.name}
                                        </span>
                                        <span className="font-mono text-[11px] tabular-nums text-fa-yellow font-bold whitespace-nowrap">
                                            £{p.price.toFixed(1)}
                                        </span>
                                    </div>
                                    <div className="font-mono text-[9px] uppercase tracking-widest text-white/50">
                                        {p.team_name} · {p.position}
                                        {p.ownership != null && <span className="ml-2 text-white/40">{p.ownership.toFixed(1)}% own</span>}
                                        {p.ppg != null && <span className="ml-2 text-white/70">PPG {p.ppg.toFixed(1)}</span>}
                                    </div>
                                </button>
                            ))}
                        </div>
                    )}
                </div>
                <p className="font-mono text-[10px] text-white/45 italic pt-2 border-t border-white/10">
                    Filter respektuje zbývající rozpočet + max {maxPerClub}/klub · řazeno dle PPG.
                </p>
            </div>
        </div>
    );
}



function StatRow({ player }) {
    const s = player.stat_line;
    const c = POS_COLOR[player.position];
    const roleBadges = (player.set_piece_roles || []).map((role) => SET_PIECE_META[role]?.short).filter(Boolean);
    return (
        <tr className="border-b border-white/5 hover:bg-white/[0.02]"
            data-testid={`result-row-${player.id}`}>
            <td className="px-2 py-1.5 font-display font-black text-sm">
                {player.name}
                {player.is_captain && <span className="font-mono text-[8px] font-bold ml-1 text-fa-yellow">(C)</span>}
                {roleBadges.length > 0 && (
                    <span className="ml-1.5 inline-flex gap-0.5">
                        {roleBadges.map((b) => (
                            <span key={b} className="font-mono text-[8px] tracking-widest text-fa-yellow bg-fa-yellow/10 px-1">{b}</span>
                        ))}
                    </span>
                )}
            </td>
            <td className={`px-1 py-1.5 font-mono text-[10px] uppercase tracking-widest ${c.text}`}>{player.position}</td>
            <td className="px-1 py-1.5 font-mono text-[10px] text-white/60">{player.team_name}</td>
            <td className="px-1 py-1.5 font-mono text-[10px] tabular-nums text-white/70">£{player.price}</td>
            <td className="px-1 py-1.5 font-mono text-[10px] tabular-nums text-fa-yellow font-bold">{player.avg_points}</td>
            <td className="px-1 py-1.5 font-mono text-[9px] tabular-nums text-white/40">{player.floor}–{player.ceiling}</td>
            <td className="px-1 py-1.5 font-mono text-[10px] tabular-nums">{s.goals}</td>
            <td className="px-1 py-1.5 font-mono text-[10px] tabular-nums">{s.assists}</td>
            <td className="px-1 py-1.5 font-mono text-[10px] tabular-nums">{s.clean_sheets}</td>
            <td className="px-1 py-1.5 font-mono text-[10px] tabular-nums text-white/60">{s.minutes}</td>
            <td className="px-1 py-1.5 font-mono text-[10px] tabular-nums">{s.bonus}</td>
            <td className="px-1 py-1.5 font-mono text-[10px] tabular-nums text-fa-mint font-bold">{s.points}</td>
        </tr>
    );
}

export default function LineupSim() {
    const [mode, setMode] = useState("fantasy");
    const [difficulty, setDifficulty] = useState("standard");
    const [budget, setBudget] = useState(100.0);
    const [nGws, setNGws] = useState(38);
    const [goalDampen, setGoalDampen] = useState(0.7);
    const [premiumBias, setPremiumBias] = useState(0.10);
    // FPL 15-man squad — slots have fixed positions (2 GKP + 5 DEF + 5 MID + 3 FWD).
    // Formation is DERIVED from which 11 slots are flagged as starters.
    // User can rearrange via drag&drop instead of picking a formation upfront.
    const [starterIdxs, setStarterIdxs] = useState(() => new Set(DEFAULT_STARTER_IDXS));
    const SLOTS = useMemo(() => SLOT_DEFS.filter((s) => starterIdxs.has(s.idx)), [starterIdxs]);
    const BENCH_SLOTS = useMemo(() => SLOT_DEFS.filter((s) => !starterIdxs.has(s.idx)), [starterIdxs]);
    const formation = useMemo(() => {
        const f = { GKP: 0, DEF: 0, MID: 0, FWD: 0 };
        for (const s of SLOTS) f[s.pos] += 1;
        return f;
    }, [SLOTS]);
    const formationKey = `${formation.DEF}-${formation.MID}-${formation.FWD}`;
    const [singleGwMode, setSingleGwMode] = useState(false);
    const [singleGwTarget, setSingleGwTarget] = useState(1);
    // Game Settings wizard — collapses to a summary bar once user clicks "Start drafting"
    const [gameStarted, setGameStarted] = useState(() => {
        try { return localStorage.getItem("lineupSim.gameStarted") === "1"; }
        catch (_) { return false; }
    });
    const [settingsOpen, setSettingsOpen] = useState(false);
    const [squad, setSquad] = useState({}); // { slotIdx: player }
    const [captainId, setCaptainId] = useState(null);
    const [setPieces, setSetPieces] = useState({
        penalty_taker: null, freekick_taker: null, corner_taker: null, long_throw_taker: null,
    });
    const [wheel, setWheel] = useState({ open: false, slot: null, candidates: [], busy: false });
    // Free-pick mode — when true, empty slots open FreePickModal (direct search)
    // instead of the random Wheel. Persisted so it survives reloads.
    const [freePick, setFreePick] = useState(() => {
        try { return localStorage.getItem("lineupSim.freePick") === "1"; }
        catch (_) { return false; }
    });
    useEffect(() => {
        try { localStorage.setItem("lineupSim.freePick", freePick ? "1" : "0"); }
        catch (_) { /* localStorage unavailable — ignore */ }
    }, [freePick]);
    useEffect(() => { track("tool_open", { tool: "lineup" }); }, []);
    const [freePickSlot, setFreePickSlot] = useState(null);
    const [swapPicker, setSwapPicker] = useState({ open: false, fromIdx: null });
    const [dragFromIdx, setDragFromIdx] = useState(null);
    const [dragOverIdx, setDragOverIdx] = useState(null);
    const [result, setResult] = useState(null);
    const [simBusy, setSimBusy] = useState(false);
    const [savedRunId, setSavedRunId] = useState(null);
    const [label, setLabel] = useState("");
    const [leaderboard, setLeaderboard] = useState([]);
    const [teams, setTeams] = useState([]);
    const [roster, setRoster] = useState([]);
    // FDR table: { current_gw, fdr: { team_id: { gw: {fdr_combined, opponent_name, is_home, ...} } } }
    const [fdrData, setFdrData] = useState(null);

    const filledIds = useMemo(() => Object.values(squad).filter(Boolean).map((p) => p.id), [squad]);
    const teamCounts = useMemo(() => {
        const tc = {};
        Object.values(squad).forEach((p) => { if (p) tc[p.team_id] = (tc[p.team_id] || 0) + 1; });
        return tc;
    }, [squad]);
    const starterEntries = useMemo(() => SLOTS.map((s) => squad[s.idx]).filter(Boolean), [SLOTS, squad]);
    const benchEntries = useMemo(() => BENCH_SLOTS.map((s) => squad[s.idx]).filter(Boolean), [BENCH_SLOTS, squad]);
    const starterSpent = useMemo(() => starterEntries.reduce((a, p) => a + p.price, 0), [starterEntries]);
    const benchSpent = useMemo(() => benchEntries.reduce((a, p) => a + p.price, 0), [benchEntries]);
    const spent = starterSpent + benchSpent;  // bench is mandatory in 15-man squad
    const remaining = useMemo(() => Math.round((budget - spent) * 10) / 10, [budget, spent]);
    const starterCount = starterEntries.length;
    const benchCount = benchEntries.length;
    const totalSlots = SLOTS.length;
    const totalBenchSlots = BENCH_SLOTS.length;
    // Valid only when ALL 15 slots are filled within budget (FPL squad rules)
    const isValid = starterCount === totalSlots
                    && benchCount === totalBenchSlots
                    && spent <= budget;

    // Load pool-info once (teams for spinner + roster for player spinner + set-piece roles)
    useEffect(() => {
        axios.get(`${API}/lineup-sim/pool-info?mode=${mode}&include_roster=true`)
            .then((r) => {
                setTeams(r.data.teams || []);
                setRoster(r.data.roster || []);
            })
            .catch(() => {});
    }, [mode]);

    // Load FDR data (default forward 5 GWs, or anchored to single GW when active)
    useEffect(() => {
        const params = singleGwMode
            ? `?n_ahead=1&from_gw=${singleGwTarget}`
            : `?n_ahead=5`;
        axios.get(`${API}/lineup-sim/fdr${params}`)
            .then((r) => setFdrData(r.data))
            .catch(() => setFdrData(null));
    }, [singleGwMode, singleGwTarget]);

    // Helper: compute FDR preview for a player's team (used on cards)
    const fdrPreviewFor = (player) => {
        if (!player || !fdrData) return null;
        const teamRow = (fdrData.fdr || {})[String(player.team_id)] || {};
        const gws = Object.keys(teamRow).sort((a, b) => parseInt(a) - parseInt(b));
        if (!gws.length) return null;
        const vals = gws.map((gw) => teamRow[gw]?.fdr_combined).filter((v) => v != null);
        if (!vals.length) return null;
        const avg = vals.reduce((a, b) => a + b, 0) / vals.length;
        const firstGw = gws[0];
        const firstRow = teamRow[firstGw] || {};
        const nextOpp = firstRow.opponent_name
            ? `${firstRow.opponent_name}${firstRow.is_home === true ? " (H)" : firstRow.is_home === false ? " (A)" : ""}`
            : null;
        const title = singleGwMode
            ? `GW${firstGw} FDR ${avg.toFixed(1)} vs ${firstRow.opponent_name || "?"}`
            : `Next ${gws.length} GWs avg FDR ${avg.toFixed(1)}`;
        return { avg, next_opp: nextOpp, title };
    };

    // Load leaderboard
    useEffect(() => {
        axios.get(`${API}/lineup-sim/leaderboard?mode=${mode}&limit=10`)
            .then((r) => setLeaderboard(r.data.rows || []))
            .catch(() => {});
    }, [mode, savedRunId]);

    // Reset squad when mode changes — different pool
    const resetSquad = () => {
        setSquad({});
        setCaptainId(null);
        setSetPieces({ penalty_taker: null, freekick_taker: null, corner_taker: null, long_throw_taker: null });
        setResult(null);
        setSavedRunId(null);
        setLabel("");
        setStarterIdxs(new Set(DEFAULT_STARTER_IDXS));
    };

    const spinWheel = async (slot) => {
        // Free-pick mode short-circuits the wheel and opens the direct picker.
        if (freePick) {
            setFreePickSlot(slot);
            return;
        }
        setWheel({ open: true, slot, candidates: [], busy: true });
        const spinStart = Date.now();
        try {
            const r = await axios.post(`${API}/lineup-sim/wheel`, {
                mode,
                slot_position: slot.pos,
                count: 4,
                excluded_ids: filledIds,
                team_counts: Object.fromEntries(Object.entries(teamCounts).map(([k, v]) => [String(k), v])),
                budget_left: remaining,
                difficulty,
                premium_bias: premiumBias,
            });
            const elapsed = Date.now() - spinStart;
            if (elapsed < 1200) {
                await new Promise((res) => setTimeout(res, 1200 - elapsed));
            }
            setWheel({ open: true, slot, candidates: r.data.candidates, busy: false });
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Wheel failed");
            setWheel({ open: false, slot: null, candidates: [], busy: false });
        }
    };

    const pickFromWheel = (cand) => {
        const filled = {
            id: cand.id,
            name: cand.name?.includes("?") ? `Mystery ${wheel.slot.pos}` : cand.name,
            team_id: cand.team_id ?? null,
            team_name: cand.team_name === "???" ? "???" : (cand.team_name || "—"),
            position: cand.position,
            price: cand.price,
            ownership: cand.ownership,
            ppg: cand.ppg,
        };
        setSquad((s) => ({ ...s, [wheel.slot.idx]: filled }));
        setWheel({ open: false, slot: null, candidates: [], busy: false });
    };

    const pickFromFreePick = (p) => {
        if (!freePickSlot) return;
        setSquad((s) => ({
            ...s,
            [freePickSlot.idx]: {
                id: p.id, name: p.name, team_id: p.team_id, team_name: p.team_name,
                position: p.position, price: p.price, ownership: p.ownership, ppg: p.ppg,
            },
        }));
        setFreePickSlot(null);
    };


    const swapSlot = (slotIdx) => {
        setSquad((s) => {
            const next = { ...s };
            const removedId = s[slotIdx]?.id;
            if (removedId === captainId) setCaptainId(null);
            // Clear any set-piece role assigned to the removed player
            if (removedId) {
                setSetPieces((sp) => {
                    const cleaned = { ...sp };
                    Object.keys(cleaned).forEach((k) => {
                        if (cleaned[k] === removedId) cleaned[k] = null;
                    });
                    return cleaned;
                });
            }
            delete next[slotIdx];
            return next;
        });
    };

    // ------------------------------------------------------------------
    // Manual swap between slots (bench ↔ starter or same-group reorder).
    // The squad has 15 fixed-position slots; the formation is dictated by
    // which 11 are flagged as starters (`starterIdxs`).
    //   - Same group (both starter or both bench): if positions match, swap the
    //     players between slots (cosmetic reorder). If positions differ, reject.
    //   - Cross group (one starter + one bench): swap their starter flags so
    //     the bench player becomes a starter and vice versa. Validate that the
    //     resulting XI is a legal FPL formation (1 GK, DEF 3-5, MID 2-5, FWD 1-3).
    // ------------------------------------------------------------------
    const ALL_SLOTS = useMemo(() => SLOT_DEFS.slice(), []);
    const slotFits = (slotMeta, player) => {
        if (!slotMeta || !player) return false;
        return slotMeta.pos === player.position;
    };

    const validateFormation = (newStarterSet) => {
        const f = { GKP: 0, DEF: 0, MID: 0, FWD: 0 };
        for (const idx of newStarterSet) {
            const slot = SLOT_DEFS.find((s) => s.idx === idx);
            if (slot) f[slot.pos] += 1;
        }
        for (const pos of ["GKP", "DEF", "MID", "FWD"]) {
            const [lo, hi] = FORMATION_LIMITS[pos];
            if (f[pos] < lo || f[pos] > hi) {
                return { ok: false, msg: `${pos} starters must be ${lo === hi ? lo : `${lo}-${hi}`} (currently ${f[pos]})` };
            }
        }
        return { ok: true };
    };

    const manualSwap = (fromIdx, toIdx) => {
        if (fromIdx == null || toIdx == null || fromIdx === toIdx) return false;
        const fromSlot = SLOT_DEFS.find((s) => s.idx === fromIdx);
        const toSlot = SLOT_DEFS.find((s) => s.idx === toIdx);
        if (!fromSlot || !toSlot) return false;
        const fromPlayer = squad[fromIdx];
        const toPlayer = squad[toIdx];
        if (!fromPlayer) return false;
        const fromIsStarter = starterIdxs.has(fromIdx);
        const toIsStarter = starterIdxs.has(toIdx);
        // CASE A: both in same group → cosmetic re-order, requires matching position
        if (fromIsStarter === toIsStarter) {
            if (fromSlot.pos !== toSlot.pos) {
                toast.error(`Same-row swap needs matching position (${fromSlot.pos} ↔ ${toSlot.pos})`);
                return false;
            }
            setSquad((s) => {
                const next = { ...s };
                if (toPlayer) {
                    next[fromIdx] = toPlayer;
                    next[toIdx] = fromPlayer;
                } else {
                    delete next[fromIdx];
                    next[toIdx] = fromPlayer;
                }
                return next;
            });
            return true;
        }
        // CASE B: cross-group → swap starter/bench roles + validate formation
        const newStarters = new Set(starterIdxs);
        if (fromIsStarter) {
            // fromIdx demoted to bench, toIdx promoted to starter
            newStarters.delete(fromIdx);
            newStarters.add(toIdx);
        } else {
            newStarters.add(fromIdx);
            newStarters.delete(toIdx);
        }
        const check = validateFormation(newStarters);
        if (!check.ok) {
            toast.error(check.msg);
            return false;
        }
        setStarterIdxs(newStarters);
        return true;
    };

    const handleDragStart = (idx) => setDragFromIdx(idx);
    const handleDragOver = (idx) => { if (dragOverIdx !== idx) setDragOverIdx(idx); };
    const handleDragLeave = (idx) => { setDragOverIdx((cur) => (cur === idx ? null : cur)); };
    const handleDrop = (toIdx) => {
        if (dragFromIdx == null) return;
        manualSwap(dragFromIdx, toIdx);
        setDragFromIdx(null);
        setDragOverIdx(null);
    };
    const openSwapPicker = (idx) => setSwapPicker({ open: true, fromIdx: idx });
    const closeSwapPicker = () => setSwapPicker({ open: false, fromIdx: null });
    const handleSwapPick = (toIdx) => {
        if (manualSwap(swapPicker.fromIdx, toIdx)) {
            closeSwapPicker();
        }
    };

    const handleSetCaptain = (player) => {
        setCaptainId(captainId === player.id ? null : player.id);
    };

    const runSim = async () => {
        if (!isValid) {
            toast.error("Fill all required slots within budget first");
            return;
        }
        setSimBusy(true);
        setResult(null);
        try {
            const starterIds = SLOTS.map((s) => squad[s.idx].id);
            const benchIds = BENCH_SLOTS.map((s) => squad[s.idx]?.id).filter(Boolean);
            const r = await axios.post(`${API}/lineup-sim/run`, {
                player_ids: starterIds,
                bench_ids: benchIds,
                mode,
                n_gws: nGws,
                sims: 120,
                goal_dampen: goalDampen,
                captain_id: captainId,
                set_pieces: setPieces,
                formation: { GKP: formation.GKP, DEF: formation.DEF, MID: formation.MID, FWD: formation.FWD },
                single_gw: singleGwMode ? singleGwTarget : null,
                budget,
            }, { timeout: 60000 });
            setResult(r.data);
            setSavedRunId(null);
            toast.success(`Simulation complete · avg ${r.data.result.total.avg}p`);
        } catch (err) {
            const detail = err?.response?.data?.detail;
            const msg = typeof detail === "string"
                ? detail
                : detail?.validation
                    ? "Squad invalid"
                    : (Array.isArray(detail) && detail[0]?.msg)
                        ? detail[0].msg
                        : "Simulation failed";
            toast.error(msg);
        } finally {
            setSimBusy(false);
        }
    };

    const saveRun = async () => {
        if (!result) return;
        try {
            const r = await axios.post(`${API}/lineup-sim/save`, {
                mode, n_gws: nGws, goal_dampen: goalDampen, captain_id: captainId,
                squad: starterEntries.map((p) => ({
                    id: p.id, name: p.name, position: p.position, team_name: p.team_name, price: p.price,
                })),
                bench: benchEntries.map((p) => ({
                    id: p.id, name: p.name, position: p.position, team_name: p.team_name, price: p.price,
                })),
                total: result.result.total,
                fpl_rank: result.result.fpl_rank,
                formation: formationKey,
                label: label || `${mode === "fantasy" ? "Fantasy" : "Standard"} ${formationKey}`,
                run_id: result.run_id,
            });
            setSavedRunId(r.data.run_id);
            toast.success("Saved · share link ready");
        } catch (err) {
            toast.error("Save failed");
        }
    };

    const createH2HChallenge = async () => {
        if (!isValid) {
            toast.error("Fill your starting XI first");
            return;
        }
        try {
            const r = await axios.post(`${API}/lineup-sim/h2h/create`, {
                owner_name: label || "Manager A",
                mode,
                formation: { GKP: formation.GKP, DEF: formation.DEF, MID: formation.MID, FWD: formation.FWD },
                budget,
                n_gws: nGws,
                goal_dampen: goalDampen,
                player_ids: SLOTS.map((s) => squad[s.idx].id),
                bench_ids: BENCH_SLOTS.map((s) => squad[s.idx]?.id).filter(Boolean),
                captain_id: captainId,
                set_pieces: setPieces,
            });
            const url = `${window.location.origin}/lineup-sim/h2h/${r.data.challenge_id}`;
            navigator.clipboard?.writeText(url);
            toast.success("Challenge link copied to clipboard");
            return url;
        } catch (err) {
            toast.error("Failed to create challenge");
        }
    };

    return (
        <AppShell>
            <section className="space-y-5">
                {/* HERO INTRO */}
                <header className="border-2 border-fa-yellow/40 bg-gradient-to-br from-fa-yellow/10 via-transparent to-fa-mint/5 p-6"
                        data-testid="lineup-sim-hero">
                    <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-fa-yellow flex items-center gap-2">
                        <SoccerBall size={11} weight="fill" />
                        Lineup Wheel · Premier League
                    </div>
                    <h1 className="font-display font-black text-3xl md:text-4xl uppercase tracking-tight mt-1">
                        Spin a squad.<br />
                        <span className="text-fa-yellow">Sim the season.</span>{" "}
                        <span className="text-white/40 text-xl">Top the board.</span>
                    </h1>
                    <p className="font-mono text-[11px] text-white/65 mt-2 max-w-2xl leading-relaxed">
                        Each slot draws <strong>4 random candidates</strong> from the PL pool under your budget
                        + max-3-per-club rule. Pick one, fill all <strong>15</strong> (XI + 4-man bench),
                        then watch a <strong>Monte Carlo</strong> roll out the season with realistic FPL stats.
                        Climb the global leaderboard.
                    </p>
                    <div className="flex gap-2 mt-3 flex-wrap">
                        <Link to="/lineup-sim/top-picks"
                              data-testid="top-picks-link"
                              className="font-mono text-[10px] uppercase tracking-widest bg-fa-yellow text-black px-3 py-1.5 hover:bg-fa-yellowHover flex items-center gap-1.5">
                            <Sparkle size={11} weight="fill" /> Top Picks Dashboard
                        </Link>
                    </div>
                </header>

                {/* GAME SETTINGS — Step 0 wizard.
                    Hidden once the user clicks "Start drafting"; re-openable via the
                    Edit button. Houses the Single-GW choice (since the user wanted it
                    in the start-of-game settings). */}
                {(!gameStarted || settingsOpen) && (
                    <div className="border-2 border-fa-yellow/60 bg-gradient-to-br from-fa-yellow/10 via-transparent to-transparent p-4 space-y-3"
                         data-testid="game-settings-card">
                        <div className="flex items-center justify-between gap-2 flex-wrap">
                            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-fa-yellow flex items-center gap-2">
                                <Sparkle size={11} weight="fill" />
                                Step 0 · Game settings
                            </div>
                            {gameStarted && (
                                <button type="button" onClick={() => setSettingsOpen(false)}
                                        data-testid="game-settings-close"
                                        className="font-mono text-[10px] uppercase tracking-widest text-white/60 hover:text-white border border-white/15 px-2 py-1">
                                    Close
                                </button>
                            )}
                        </div>
                        <h2 className="font-display font-black text-2xl uppercase tracking-tight">
                            Configure your run
                        </h2>
                        <p className="font-mono text-[11px] text-white/65 leading-relaxed max-w-2xl">
                            Pick the <strong>player pool</strong> (Fantasy = top-200 most relevant, Standard = full PL),
                            and decide whether you want to sim the <strong>whole season</strong> or just a <strong>single
                            gameweek</strong> (heavy-tailed haul model — most players blank, a few haul).
                        </p>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 pt-1">
                            <div className="border border-white/10 bg-black/40 p-3 space-y-1.5">
                                <div className="font-mono text-[9px] uppercase tracking-widest text-white/45">Player pool</div>
                                <div className="flex gap-1.5">
                                    {[["fantasy", "Fantasy"], ["standard", "Standard"]].map(([k, l]) => (
                                        <button key={k} type="button" onClick={() => { setMode(k); resetSquad(); }}
                                                data-testid={`gs-mode-${k}`}
                                                className={`font-mono text-[10px] uppercase tracking-widest px-3 py-1.5 ${mode === k ? "bg-fa-yellow text-black font-bold" : "bg-white/[0.03] text-white/60 border border-white/10 hover:text-white"}`}>
                                            {l}
                                        </button>
                                    ))}
                                </div>
                            </div>
                            <div className="border border-white/10 bg-black/40 p-3 space-y-1.5">
                                <div className="font-mono text-[9px] uppercase tracking-widest text-white/45">Simulation horizon</div>
                                <div className="flex items-center gap-2">
                                    <button type="button" onClick={() => setSingleGwMode(false)}
                                            data-testid="gs-horizon-season"
                                            className={`font-mono text-[10px] uppercase tracking-widest px-3 py-1.5 ${!singleGwMode ? "bg-fa-yellow text-black font-bold" : "bg-white/[0.03] text-white/60 border border-white/10 hover:text-white"}`}>
                                        Season {nGws}GW
                                    </button>
                                    <button type="button" onClick={() => setSingleGwMode(true)}
                                            data-testid="gs-horizon-single"
                                            className={`font-mono text-[10px] uppercase tracking-widest px-3 py-1.5 ${singleGwMode ? "bg-fa-yellow text-black font-bold" : "bg-white/[0.03] text-white/60 border border-white/10 hover:text-white"}`}>
                                        Single GW
                                    </button>
                                </div>
                                {singleGwMode && (
                                    <label className="font-mono text-[10px] uppercase tracking-widest text-white/55 flex items-center gap-1.5">
                                        GW
                                        <input type="number" min={1} max={38} value={singleGwTarget}
                                               onChange={(e) => setSingleGwTarget(Math.max(1, Math.min(38, parseInt(e.target.value) || 1)))}
                                               data-testid="gs-gw-input"
                                               className="bg-black/40 border border-white/15 px-1.5 py-0.5 w-14 text-white text-[11px] tabular-nums focus:outline-none focus:border-fa-yellow"/>
                                    </label>
                                )}
                            </div>
                            <div className="border border-white/10 bg-black/40 p-3 space-y-1.5">
                                <div className="font-mono text-[9px] uppercase tracking-widest text-white/45">Budget</div>
                                <div className="flex items-center gap-1.5">
                                    <span className="font-mono text-[11px] text-white/65">£</span>
                                    <input type="number" value={budget} min={80} max={120} step={0.5}
                                           onChange={(e) => setBudget(parseFloat(e.target.value) || 100)}
                                           data-testid="gs-budget-input"
                                           className="bg-black/40 border border-white/15 px-1.5 py-0.5 w-20 text-white text-[12px] tabular-nums focus:outline-none focus:border-fa-yellow"/>
                                    <span className="font-mono text-[11px] text-white/45">m</span>
                                </div>
                            </div>
                        </div>
                        <button type="button"
                                onClick={() => {
                                    setGameStarted(true);
                                    setSettingsOpen(false);
                                    try { localStorage.setItem("lineupSim.gameStarted", "1"); } catch (_) { /* ignore */ }
                                }}
                                data-testid="start-drafting-btn"
                                className="w-full bg-fa-yellow text-black font-display font-black text-base uppercase tracking-tight py-2.5 hover:bg-fa-yellowHover flex items-center justify-center gap-2">
                            <Lightning size={16} weight="fill" />
                            {gameStarted ? "Save settings" : "Start drafting →"}
                        </button>
                    </div>
                )}
                {gameStarted && !settingsOpen && (
                    <div className="border border-fa-yellow/30 bg-fa-yellow/[0.04] px-4 py-2 flex items-center justify-between gap-3 flex-wrap"
                         data-testid="game-settings-summary">
                        <div className="font-mono text-[10px] uppercase tracking-widest text-white/70 flex items-center gap-3 flex-wrap">
                            <span className="text-fa-yellow flex items-center gap-1.5">
                                <Sparkle size={10} weight="fill" /> Settings
                            </span>
                            <span>Pool: <strong className="text-white">{mode}</strong></span>
                            <span>Horizon: <strong className="text-white">{singleGwMode ? `GW${singleGwTarget}` : `${nGws}-GW season`}</strong></span>
                            <span>Budget: <strong className="text-white">£{budget}m</strong></span>
                            <span>Formation: <strong className="text-white">{formationKey}</strong></span>
                        </div>
                        <button type="button" onClick={() => setSettingsOpen(true)}
                                data-testid="edit-settings-btn"
                                className="font-mono text-[10px] uppercase tracking-widest text-fa-yellow border border-fa-yellow/40 px-3 py-1 hover:bg-fa-yellow hover:text-black">
                            <ArrowsClockwise size={10} weight="bold" className="inline mb-0.5 mr-1" />
                            Edit settings
                        </button>
                    </div>
                )}

                {/* CONTROL BAR */}
                <div className="border border-white/10 bg-[#0d0d0d] p-3 space-y-3" data-testid="control-bar">
                    <div className="flex items-center gap-4 flex-wrap">
                        {/* Mode */}
                        <div className="flex items-center gap-1.5">
                            <span className="font-mono text-[10px] uppercase tracking-widest text-white/50">Pool</span>
                            {[["fantasy", "Fantasy"], ["standard", "Standard"]].map(([k, l]) => (
                                <button key={k} type="button" onClick={() => { setMode(k); resetSquad(); }}
                                        data-testid={`mode-${k}`}
                                        className={`font-mono text-[10px] uppercase tracking-widest px-3 py-1.5 flex items-center gap-1 ${mode === k ? "bg-fa-yellow text-black" : "bg-white/[0.03] text-white/55 hover:text-white border border-white/10"}`}>
                                    {k === "fantasy" && <Sparkle size={10} weight={mode === k ? "fill" : "regular"} />}
                                    {l}
                                </button>
                            ))}
                        </div>
                        {/* Pick mode: Wheel (random) vs Free pick (direct search) */}
                        <div className="flex items-center gap-1.5" data-testid="pick-mode-toggle">
                            <span className="font-mono text-[10px] uppercase tracking-widest text-white/50">Výběr</span>
                            <button type="button" onClick={() => setFreePick(false)}
                                    data-testid="pick-mode-wheel"
                                    title="Kolo štěstí — každý slot nabídne 4 náhodné kandidáty"
                                    className={`font-mono text-[10px] uppercase tracking-widest px-3 py-1.5 flex items-center gap-1 ${!freePick ? "bg-fa-yellow text-black" : "bg-white/[0.03] text-white/55 hover:text-white border border-white/10"}`}>
                                <ArrowsClockwise size={10} weight={!freePick ? "fill" : "regular"} />
                                Wheel
                            </button>
                            <button type="button" onClick={() => setFreePick(true)}
                                    data-testid="pick-mode-free"
                                    title="Volný výběr — vyhledej si konkrétního hráče přímo (bez losování)"
                                    className={`font-mono text-[10px] uppercase tracking-widest px-3 py-1.5 flex items-center gap-1 ${freePick ? "bg-fa-mint text-black" : "bg-white/[0.03] text-white/55 hover:text-white border border-white/10"}`}>
                                <Plus size={10} weight={freePick ? "bold" : "regular"} />
                                Free pick
                            </button>
                        </div>
                        {/* Difficulty */}
                        <div className="flex items-center gap-1.5">
                            <span className="font-mono text-[10px] uppercase tracking-widest text-white/50">Difficulty</span>
                            {Object.entries(DIFFICULTY_INFO).map(([k, info]) => {
                                const Icon = info.icon;
                                return (
                                    <button key={k} type="button" onClick={() => setDifficulty(k)}
                                            data-testid={`diff-${k}`}
                                            title={info.hint}
                                            className={`font-mono text-[10px] uppercase tracking-widest px-3 py-1.5 flex items-center gap-1 ${difficulty === k ? "bg-fa-yellow text-black" : "bg-white/[0.03] text-white/55 hover:text-white border border-white/10"}`}>
                                        <Icon size={10} weight={difficulty === k ? "fill" : "regular"} />
                                        {info.label}
                                    </button>
                                );
                            })}
                        </div>
                        {/* Budget + GWs + dampener */}
                        <div className="flex items-center gap-2 ml-auto">
                            <label className="font-mono text-[10px] uppercase tracking-widest text-white/50 flex items-center gap-1.5">
                                Budget £
                                <input type="number" value={budget} min={80} max={120} step={0.5}
                                       onChange={(e) => setBudget(parseFloat(e.target.value) || 100)}
                                       data-testid="budget-input"
                                       className="bg-black/40 border border-white/15 px-1.5 py-0.5 w-16 text-white text-[11px] tabular-nums focus:outline-none focus:border-fa-yellow"/>m
                            </label>
                            <label className="font-mono text-[10px] uppercase tracking-widest text-white/50 flex items-center gap-1.5">
                                GWs
                                <input type="number" value={nGws} min={5} max={38}
                                       onChange={(e) => setNGws(parseInt(e.target.value) || 38)}
                                       data-testid="gws-input"
                                       className="bg-black/40 border border-white/15 px-1.5 py-0.5 w-12 text-white text-[11px] tabular-nums focus:outline-none focus:border-fa-yellow"/>
                            </label>
                            <label className="font-mono text-[10px] uppercase tracking-widest text-white/50 flex items-center gap-1.5" title="Lower = fewer goals">
                                Goal ×
                                <input type="number" value={goalDampen} min={0.3} max={1.2} step={0.05}
                                       onChange={(e) => setGoalDampen(parseFloat(e.target.value) || 0.7)}
                                       data-testid="dampen-input"
                                       className="bg-black/40 border border-white/15 px-1.5 py-0.5 w-14 text-white text-[11px] tabular-nums focus:outline-none focus:border-fa-yellow"/>
                            </label>
                            <label className="font-mono text-[10px] uppercase tracking-widest text-white/50 flex items-center gap-1.5" title="Higher = pricier players appear more often on the wheel">
                                Premium ×
                                <input type="number" value={premiumBias} min={0} max={0.3} step={0.02}
                                       onChange={(e) => setPremiumBias(parseFloat(e.target.value) || 0)}
                                       data-testid="premium-input"
                                       className="bg-black/40 border border-white/15 px-1.5 py-0.5 w-14 text-white text-[11px] tabular-nums focus:outline-none focus:border-fa-yellow"/>
                            </label>
                        </div>
                    </div>
                    {/* Current formation (derived) + reset */}
                    <div className="flex items-center gap-3 flex-wrap pt-2 border-t border-white/5">
                        <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-white/55"
                             data-testid="current-formation">
                            <span className="text-white/40">Formation</span>
                            <span className="font-display font-black text-base text-fa-yellow tabular-nums">
                                {formationKey}
                            </span>
                            <span className="text-white/30">·</span>
                            <span className="text-white/40 italic">
                                drag bench ↔ XI to reshape
                            </span>
                        </div>
                        <button type="button" onClick={resetSquad} data-testid="reset-btn"
                                disabled={starterCount === 0 && benchCount === 0 && !result}
                                className="ml-auto font-mono text-[10px] uppercase tracking-widest text-red-300 border border-red-300/40 px-3 py-1.5 hover:bg-red-300 hover:text-black disabled:opacity-30 disabled:cursor-not-allowed flex items-center gap-1.5 transition-colors">
                            <X size={11} weight="bold" />
                            Reset squad
                        </button>
                    </div>
                    <p className="font-mono text-[10px] text-white/35 italic">
                        <Brain size={10} weight="bold" className="inline mb-0.5 mr-1" />
                        {DIFFICULTY_INFO[difficulty].hint}
                    </p>
                </div>

                {/* SQUAD STATUS */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="squad-status">
                    <div className="border border-white/10 bg-[#0d0d0d] p-3">
                        <div className="font-mono text-[9px] uppercase tracking-widest text-white/40">15-man squad</div>
                        <div className="font-display font-black text-2xl tabular-nums">
                            <span className={(starterCount + benchCount) === 15 ? "text-fa-mint" : "text-white"}>{starterCount + benchCount}</span>
                            <span className="text-white/30 text-base"> / 15</span>
                        </div>
                        <div className="font-mono text-[9px] text-white/40 mt-0.5">
                            XI {starterCount}/{totalSlots} · Bench {benchCount}/{totalBenchSlots}
                        </div>
                    </div>
                    <div className="border border-white/10 bg-[#0d0d0d] p-3">
                        <div className="font-mono text-[9px] uppercase tracking-widest text-white/40">Spent / Budget</div>
                        <div className="font-display font-black text-2xl tabular-nums">
                            £{spent.toFixed(1)}
                            <span className="text-white/30 text-base"> / {budget}m</span>
                        </div>
                    </div>
                    <div className="border border-white/10 bg-[#0d0d0d] p-3">
                        <div className="font-mono text-[9px] uppercase tracking-widest text-white/40">Remaining</div>
                        <div className={`font-display font-black text-2xl tabular-nums ${remaining < 0 ? "text-red-300" : remaining < 5 ? "text-fa-yellow" : "text-fa-mint"}`}>
                            £{remaining.toFixed(1)}m
                        </div>
                    </div>
                    <div className="border border-white/10 bg-[#0d0d0d] p-3">
                        <div className="font-mono text-[9px] uppercase tracking-widest text-white/40">Captain</div>
                        <div className="font-display font-black text-base truncate text-fa-yellow">
                            {captainId ? Object.values(squad).find((p) => p?.id === captainId)?.name : "—"}
                        </div>
                    </div>
                </div>

                {/* PITCH (slots depend on selected formation) */}
                <div className="border border-white/10 bg-gradient-to-b from-fa-mint/[0.04] to-transparent p-4 space-y-3"
                     data-testid="pitch">
                    <div className="font-mono text-[9px] uppercase tracking-widest text-white/35 italic flex items-center gap-1.5"
                         data-testid="swap-hint">
                        <Swap size={10} weight="fill" className="text-fa-mint" />
                        Tip: drag a player onto another slot, or hover and click <span className="text-fa-yellow">↔ SWAP</span> to pick a target.
                    </div>
                    {["FWD", "MID", "DEF", "GKP"].map((rowPos) => {
                        const rowSlots = SLOTS.filter((s) => s.pos === rowPos);
                        if (!rowSlots.length) return null;
                        const n = rowSlots.length;
                        const gridClass =
                            n === 1 ? "grid-cols-1 max-w-xs mx-auto" :
                            n === 2 ? "grid-cols-2 max-w-2xl mx-auto" :
                            n === 3 ? "grid-cols-3 max-w-3xl mx-auto" :
                            n === 4 ? "grid-cols-4" :
                            "grid-cols-5";
                        return (
                            <div key={rowPos} className={`grid gap-2 ${gridClass}`}>
                                {rowSlots.map((slot) => {
                                    const player = squad[slot.idx];
                                    return player ? (
                                        <PlayerCard key={slot.idx} player={player}
                                                    slotIdx={slot.idx}
                                                    isCaptain={captainId === player.id}
                                                    isDragOver={dragOverIdx === slot.idx}
                                                    fdrPreview={fdrPreviewFor(player)}
                                                    onSetCaptain={() => handleSetCaptain(player)}
                                                    onRemove={() => swapSlot(slot.idx)}
                                                    onOpenSwap={openSwapPicker}
                                                    onDragStart={handleDragStart}
                                                    onDragOver={handleDragOver}
                                                    onDragLeave={handleDragLeave}
                                                    onDrop={handleDrop}/>
                                    ) : (
                                        <EmptySlot key={slot.idx} pos={slot.pos}
                                                   slotIdx={slot.idx}
                                                   isDragOver={dragOverIdx === slot.idx}
                                                   onSpin={() => spinWheel(slot)}
                                                   onDragOver={handleDragOver}
                                                   onDragLeave={handleDragLeave}
                                                   onDrop={handleDrop}/>
                                    );
                                })}
                            </div>
                        );
                    })}
                </div>
                {/* BENCH SECTION (sibling of pitch for clean scoping) */}
                <div className="border border-white/10 bg-gradient-to-b from-white/[0.02] to-transparent p-4 space-y-2"
                     data-testid="bench-section">
                    <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2">
                            <span className="font-mono text-[10px] uppercase tracking-widest text-white/60 flex items-center gap-1.5">
                                <Swap size={11} weight="fill" className="text-fa-mint" />
                                Bench {benchCount}/{totalBenchSlots} (auto-swap on DNP)
                                </span>
                                <span className="font-mono text-[9px] uppercase tracking-widest text-fa-mint">
                                    £{benchSpent.toFixed(1)}m bench value
                                </span>
                            </div>
                            <span className="font-mono text-[9px] uppercase tracking-widest text-white/40 italic">
                                15-man squad · 2 GKP · 5 DEF · 5 MID · 3 FWD
                            </span>
                        </div>
                        <div className={`grid gap-2 ${totalBenchSlots <= 4 ? "grid-cols-4" : "grid-cols-5"}`}>
                            {BENCH_SLOTS.map((slot) => {
                                const player = squad[slot.idx];
                                return player ? (
                                    <PlayerCard key={slot.idx} player={player}
                                                slotIdx={slot.idx}
                                                isCaptain={false}
                                                isDragOver={dragOverIdx === slot.idx}
                                                fdrPreview={fdrPreviewFor(player)}
                                                onSetCaptain={() => {}}
                                                onRemove={() => swapSlot(slot.idx)}
                                                onOpenSwap={openSwapPicker}
                                                onDragStart={handleDragStart}
                                                onDragOver={handleDragOver}
                                                onDragLeave={handleDragLeave}
                                                onDrop={handleDrop}/>
                                ) : (
                                    <EmptySlot key={slot.idx} pos={slot.pos}
                                               slotIdx={slot.idx}
                                               isDragOver={dragOverIdx === slot.idx}
                                               onSpin={() => spinWheel(slot)}
                                               onDragOver={handleDragOver}
                                               onDragLeave={handleDragLeave}
                                               onDrop={handleDrop}/>
                                );
                            })}
                        </div>
                </div>

                {/* SINGLE-GW MODE TOGGLE */}
                <div className="border border-white/10 bg-[#0d0d0d] p-3 flex items-center gap-4 flex-wrap"
                     data-testid="single-gw-toggle">
                    <label className="font-mono text-[10px] uppercase tracking-widest text-white/55 flex items-center gap-2 cursor-pointer">
                        <input type="checkbox" checked={singleGwMode}
                               onChange={(e) => setSingleGwMode(e.target.checked)}
                               data-testid="single-gw-checkbox"
                               className="accent-fa-yellow"/>
                        <Lightning size={11} weight="fill" className="text-fa-yellow" />
                        Single-gameweek mode
                    </label>
                    {singleGwMode && (
                        <label className="font-mono text-[10px] uppercase tracking-widest text-white/55 flex items-center gap-1.5">
                            GW
                            <input type="number" min={1} max={38} value={singleGwTarget}
                                   onChange={(e) => setSingleGwTarget(parseInt(e.target.value) || 1)}
                                   data-testid="single-gw-input"
                                   className="bg-black/40 border border-white/15 px-1.5 py-0.5 w-14 text-white text-[11px] tabular-nums focus:outline-none focus:border-fa-yellow"/>
                        </label>
                    )}
                    <span className="font-mono text-[10px] text-white/40 italic">
                        {singleGwMode
                            ? `Will simulate only GW${singleGwTarget} (1 match per player) — quick variance check`
                            : `Will simulate the full ${nGws}-gameweek season`}
                    </span>
                </div>

                {/* SET-PIECE ROLES */}
                <SetPiecesPanel squad={squad} setPieces={setPieces}
                                onChange={setSetPieces}
                                enabled={starterCount === totalSlots}/>

                {/* RUN SIM BUTTON */}
                <button type="button" onClick={runSim} disabled={!isValid || simBusy}
                        data-testid="run-sim-btn"
                        className="w-full bg-fa-yellow text-black font-display font-black text-lg uppercase tracking-tight py-3 hover:bg-fa-yellowHover disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2">
                    {simBusy
                        ? <><Spinner size={18} weight="bold" className="animate-spin" /> Simulating {singleGwMode ? `GW${singleGwTarget}` : `${nGws} GWs`}…</>
                        : isValid
                            ? <><Lightning size={18} weight="fill" /> Run {singleGwMode ? `GW${singleGwTarget}` : "season"} simulation</>
                            : (() => {
                                const remainingSlots = (totalSlots - starterCount) + (totalBenchSlots - benchCount);
                                return <>Fill {remainingSlots} more slot{remainingSlots === 1 ? "" : "s"} to simulate</>;
                            })()}
                </button>

                {/* RESULT */}
                {result?.result && (
                    <div className="space-y-3" data-testid="sim-result">
                        {result.result.engine === "haul_v1" && (
                            <div className="border border-fa-yellow/40 bg-fa-yellow/[0.05] p-3 flex items-start gap-3"
                                 data-testid="haul-banner">
                                <Sparkle size={16} weight="fill" className="text-fa-yellow mt-0.5" />
                                <div className="flex-1">
                                    <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-fa-yellow mb-0.5">
                                        Haul model · GW{result.result.fdr_target_gw || result.result.single_gw}
                                    </div>
                                    <p className="font-mono text-[10px] text-white/65 leading-relaxed">
                                        Samples from each player&apos;s real per-GW history (this season + 25% blend
                                        from last season, skipping injury years). Nudged by FDR (FPL official + custom)
                                        and a forward-looking prior boost — young players get +20%, veterans -3.5 to -5%,
                                        injury-year tolerance (à la Muniz/Mateta) forgives one bad season.
                                    </p>
                                </div>
                            </div>
                        )}
                        {result.result.engine !== "haul_v1" && result.result.fdr_applied && (
                            <div className="border border-fa-mint/40 bg-fa-mint/[0.05] p-3 flex items-start gap-3"
                                 data-testid="fdr-applied-banner">
                                <Sparkle size={16} weight="fill" className="text-fa-mint mt-0.5" />
                                <div className="flex-1">
                                    <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-fa-mint mb-0.5">
                                        FDR applied · forward {result.result.n_gws} GWs from GW{result.result.start_gw}
                                    </div>
                                    <p className="font-mono text-[10px] text-white/65 leading-relaxed">
                                        Each GW&apos;s per-player goal/assist/CS rates are nudged by the
                                        opponent&apos;s combined FDR (official FPL difficulty + custom strength delta).
                                        Defenders/GKs are 25% more sensitive to FDR than attackers.
                                    </p>
                                </div>
                            </div>
                        )}
                        <div className="border-2 border-fa-mint/40 bg-fa-mint/[0.06] p-4">
                            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-fa-mint flex items-center gap-2 mb-2">
                                <Trophy size={11} weight="fill" /> Simulation result · {result.result.sims} runs × {result.result.n_gws} GWs
                            </div>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                <div>
                                    <div className="font-mono text-[9px] uppercase tracking-widest text-white/40">Avg total</div>
                                    <div className="font-display font-black text-3xl text-fa-mint tabular-nums">{result.result.total.avg}<span className="text-white/30 text-lg">p</span></div>
                                </div>
                                <div>
                                    <div className="font-mono text-[9px] uppercase tracking-widest text-white/40">Median</div>
                                    <div className="font-display font-black text-2xl text-white tabular-nums">{result.result.total.median}p</div>
                                </div>
                                <div>
                                    <div className="font-mono text-[9px] uppercase tracking-widest text-white/40">Ceiling (P90)</div>
                                    <div className="font-display font-black text-2xl text-fa-yellow tabular-nums">{result.result.total.ceiling}p</div>
                                </div>
                                <div>
                                    <div className="font-mono text-[9px] uppercase tracking-widest text-white/40">Floor (P10)</div>
                                    <div className="font-display font-black text-2xl text-white/50 tabular-nums">{result.result.total.floor}p</div>
                                </div>
                            </div>
                        </div>

                        {/* FPL OVERALL RANK */}
                        {result.result.fpl_rank && (
                            <div className="border-2 border-fa-yellow/40 bg-gradient-to-r from-fa-yellow/10 via-transparent to-transparent p-4"
                                 data-testid="fpl-rank-card">
                                <div className="flex items-center justify-between flex-wrap gap-2">
                                    <div>
                                        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-fa-yellow flex items-center gap-1.5">
                                            <Trophy size={11} weight="fill" /> FPL overall rank estimate
                                        </div>
                                        <div className="font-display font-black text-2xl md:text-3xl uppercase tracking-tight mt-1 text-fa-yellow">
                                            {result.result.fpl_rank.label}
                                        </div>
                                        <p className="font-mono text-[10px] text-white/55 mt-1 max-w-xl">
                                            Based on rolling 5-year FPL leaderboard averages
                                            (~{(result.result.fpl_rank.total_managers / 1_000_000).toFixed(0)}M managers per season).
                                            Your XI&apos;s avg ({result.result.total.avg}p) maps to roughly position{" "}
                                            <strong className="text-white">≈{result.result.fpl_rank.approx_rank.toLocaleString()}</strong>.
                                        </p>
                                    </div>
                                    <div className="text-right">
                                        <div className="font-mono text-[9px] uppercase tracking-widest text-white/40">Better than</div>
                                        <div className="font-display font-black text-3xl text-fa-mint tabular-nums">
                                            {result.result.fpl_rank.percentile}<span className="text-white/30 text-lg">%</span>
                                        </div>
                                        <div className="font-mono text-[9px] uppercase tracking-widest text-white/40">of managers</div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* PER-GW BREAKDOWN */}
                        {result.result.per_gw?.length > 0 && (
                            <details className="border border-white/10 bg-[#0d0d0d]" open
                                     data-testid="per-gw-block">
                                <summary className="cursor-pointer list-none px-3 py-2 border-b border-white/10 flex items-center gap-2 hover:bg-white/[0.03]">
                                    <ArrowsClockwise size={11} weight="fill" className="text-fa-yellow" />
                                    <span className="font-mono text-[10px] uppercase tracking-widest text-fa-yellow">
                                        Gameweek breakdown · {result.result.per_gw.length} GWs
                                    </span>
                                    <span className="ml-auto font-mono text-[10px] text-white/40">avg {(result.result.total.avg / result.result.per_gw.length).toFixed(1)}p / GW</span>
                                </summary>
                                <div className="overflow-x-auto max-h-[420px] overflow-y-auto">
                                    <table className="w-full">
                                        <thead className="bg-white/[0.02] sticky top-0">
                                            <tr className="font-mono text-[9px] uppercase tracking-widest text-white/40">
                                                <th className="px-3 py-1.5 text-left">GW</th>
                                                <th className="px-3 py-1.5 text-right">Total pts</th>
                                                <th className="px-3 py-1.5 text-left">Top scorer</th>
                                                <th className="px-3 py-1.5 text-right">Top pts</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {result.result.per_gw.map((gw) => (
                                                <tr key={gw.gw} className="border-b border-white/5 hover:bg-white/[0.02]"
                                                    data-testid={`gw-row-${gw.gw}`}>
                                                    <td className="px-3 py-1.5 font-display font-black text-fa-yellow tabular-nums">
                                                        GW{gw.gw}
                                                    </td>
                                                    <td className="px-3 py-1.5 font-mono text-[11px] tabular-nums text-fa-mint font-bold text-right">
                                                        {gw.total_pts}p
                                                    </td>
                                                    <td className="px-3 py-1.5 font-mono text-[11px]">
                                                        {gw.top_scorer.name}
                                                        {gw.top_scorer.is_captain && <span className="font-mono text-[8px] font-bold ml-1 text-fa-yellow">(C)</span>}
                                                    </td>
                                                    <td className="px-3 py-1.5 font-mono text-[11px] tabular-nums text-fa-yellow text-right">
                                                        {gw.top_scorer.points}p
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </details>
                        )}

                        <div className="border border-white/10 bg-[#0d0d0d] overflow-x-auto">
                            {result.result.engine === "haul_v1" ? (
                                <table className="w-full text-left" data-testid="haul-table">
                                    <thead className="bg-white/[0.02] border-b border-white/10">
                                        <tr className="font-mono text-[9px] uppercase tracking-widest text-white/40">
                                            <th className="px-2 py-2">Name</th>
                                            <th className="px-1 py-2">Pos</th>
                                            <th className="px-1 py-2">Team</th>
                                            <th className="px-1 py-2">£</th>
                                            <th className="px-1 py-2">vs</th>
                                            <th className="px-1 py-2" title="Combined FDR (1=easy, 5=hard)">FDR</th>
                                            <th className="px-1 py-2">Avg pts</th>
                                            <th className="px-1 py-2">Range</th>
                                            <th className="px-1 py-2" title="% of past GWs with ≤2 pts">Blank%</th>
                                            <th className="px-1 py-2" title="% of past GWs with ≥10 pts">Haul%</th>
                                            <th className="px-1 py-2" title="Games in this season's sample">N</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {result.result.players.map((p) => {
                                            const c = POS_COLOR[p.position];
                                            const fdr = p.fdr_combined;
                                            const fdrColor = fdr == null ? "text-white/40"
                                                : fdr < 2.5 ? "text-fa-mint"
                                                : fdr < 3.5 ? "text-fa-yellow"
                                                : "text-red-300";
                                            return (
                                                <tr key={p.id} className="border-b border-white/5 hover:bg-white/[0.02]"
                                                    data-testid={`haul-row-${p.id}`}>
                                                    <td className="px-2 py-1.5 font-display font-black text-sm">
                                                        {p.name}{p.is_captain && <span className="font-mono text-[8px] font-bold ml-1 text-fa-yellow">(C)</span>}
                                                        {p.prior_boost != null && p.prior_boost !== 1.0 && (
                                                            <span className={`ml-1 font-mono text-[8px] font-bold ${p.prior_boost > 1.0 ? "text-fa-mint" : "text-orange-300"}`}
                                                                  title={(p.prior_notes || []).join(" · ") || `Prior boost ${p.prior_boost}x`}
                                                                  data-testid={`prior-badge-${p.id}`}>
                                                                {p.prior_boost > 1.0 ? "↑" : "↓"}{Math.round(Math.abs(p.prior_boost - 1) * 100)}%
                                                            </span>
                                                        )}
                                                        {p.injury_year_last && (
                                                            <span className="ml-1 font-mono text-[8px] text-orange-200 bg-orange-500/15 px-1"
                                                                  title="Last season looked injury-affected — excluded from baseline">
                                                                INJ
                                                            </span>
                                                        )}
                                                    </td>
                                                    <td className={`px-1 py-1.5 font-mono text-[10px] uppercase tracking-widest ${c?.text || "text-white/70"}`}>{p.position}</td>
                                                    <td className="px-1 py-1.5 font-mono text-[10px] text-white/60">{p.team_name}</td>
                                                    <td className="px-1 py-1.5 font-mono text-[10px] tabular-nums text-white/70">£{p.price}</td>
                                                    <td className="px-1 py-1.5 font-mono text-[10px] text-white/60 truncate max-w-[120px]">
                                                        {p.opponent_name || "—"}{p.is_home === true ? " (H)" : p.is_home === false ? " (A)" : ""}
                                                    </td>
                                                    <td className={`px-1 py-1.5 font-mono text-[10px] tabular-nums font-bold ${fdrColor}`}>
                                                        {fdr != null ? fdr.toFixed(1) : "—"}
                                                    </td>
                                                    <td className="px-1 py-1.5 font-mono text-[10px] tabular-nums text-fa-yellow font-bold">{p.avg_points}</td>
                                                    <td className="px-1 py-1.5 font-mono text-[9px] tabular-nums text-white/40">{p.floor}–{p.ceiling}</td>
                                                    <td className="px-1 py-1.5 font-mono text-[10px] tabular-nums text-white/60">
                                                        {p.blank_rate != null ? `${Math.round(p.blank_rate * 100)}%` : "—"}
                                                    </td>
                                                    <td className="px-1 py-1.5 font-mono text-[10px] tabular-nums text-fa-mint">
                                                        {p.haul_rate != null ? `${Math.round(p.haul_rate * 100)}%` : "—"}
                                                    </td>
                                                    <td className="px-1 py-1.5 font-mono text-[10px] tabular-nums text-white/40">{p.history_games || 0}</td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            ) : (
                                <table className="w-full text-left">
                                    <thead className="bg-white/[0.02] border-b border-white/10">
                                        <tr className="font-mono text-[9px] uppercase tracking-widest text-white/40">
                                            <th className="px-2 py-2">Name</th>
                                            <th className="px-1 py-2">Pos</th>
                                            <th className="px-1 py-2">Team</th>
                                            <th className="px-1 py-2">£</th>
                                            <th className="px-1 py-2">Avg pts</th>
                                            <th className="px-1 py-2">Range</th>
                                            <th className="px-1 py-2">G</th>
                                            <th className="px-1 py-2">A</th>
                                            <th className="px-1 py-2">CS</th>
                                            <th className="px-1 py-2">Min</th>
                                            <th className="px-1 py-2">Bonus</th>
                                            <th className="px-1 py-2">Pts</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {result.result.players.map((p) => <StatRow key={p.id} player={p} />)}
                                    </tbody>
                                </table>
                            )}
                        </div>

                        {/* Save / Share / H2H block */}
                        <div className="border border-fa-yellow/30 bg-fa-yellow/5 p-3 space-y-2"
                             data-testid="save-block">
                            <div className="flex items-center gap-2 flex-wrap">
                                {savedRunId ? (
                                    <span className="font-mono text-[11px] text-fa-mint flex items-center gap-1.5">
                                        <Trophy size={12} weight="fill" />
                                        Saved · run id {savedRunId.slice(0, 8)}…
                                    </span>
                                ) : (
                                    <>
                                        <input type="text" value={label} onChange={(e) => setLabel(e.target.value)}
                                               placeholder="Name your squad (optional)"
                                               data-testid="save-label"
                                               className="flex-1 bg-black/40 border border-white/15 px-2 py-1.5 font-mono text-[11px] focus:outline-none focus:border-fa-yellow"/>
                                        <button type="button" onClick={saveRun} data-testid="save-btn"
                                                className="font-mono text-[11px] uppercase tracking-widest font-bold bg-fa-yellow text-black px-3 py-1.5 hover:bg-fa-yellowHover flex items-center gap-1.5">
                                            <FloppyDisk size={12} weight="fill" /> Save to leaderboard
                                        </button>
                                    </>
                                )}
                            </div>
                            {/* Share + H2H actions */}
                            <div className="flex items-center gap-2 flex-wrap pt-2 border-t border-fa-yellow/15">
                                {savedRunId && (() => {
                                    const shareUrl = `${window.location.origin}/lineup-sim/share/${savedRunId}`;
                                    const shareText = `My lineup sim averaged ${result.result.total.avg}p — ${result.result.fpl_rank?.label || "ranked"}`;
                                    return (
                                        <>
                                            <button type="button" onClick={() => {
                                                navigator.clipboard?.writeText(shareUrl);
                                                toast.success("Link copied");
                                            }} data-testid="share-copy-btn"
                                                    className="font-mono text-[10px] uppercase tracking-widest border border-white/20 text-white/80 hover:bg-white/10 px-3 py-1.5 flex items-center gap-1.5">
                                                <ShareNetwork size={11} weight="bold" /> Copy share link
                                            </button>
                                            <a href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`}
                                               target="_blank" rel="noreferrer" data-testid="share-twitter"
                                               className="font-mono text-[10px] uppercase tracking-widest border border-white/20 text-white/80 hover:bg-white/10 px-3 py-1.5 flex items-center gap-1.5">
                                                <TwitterLogo size={11} weight="fill" /> X / Twitter
                                            </a>
                                            <a href={`https://wa.me/?text=${encodeURIComponent(shareText + " " + shareUrl)}`}
                                               target="_blank" rel="noreferrer" data-testid="share-whatsapp"
                                               className="font-mono text-[10px] uppercase tracking-widest border border-fa-mint/40 text-fa-mint hover:bg-fa-mint/10 px-3 py-1.5 flex items-center gap-1.5">
                                                <WhatsappLogo size={11} weight="fill" /> WhatsApp
                                            </a>
                                        </>
                                    );
                                })()}
                                <button type="button" onClick={createH2HChallenge}
                                        data-testid="h2h-create-btn"
                                        disabled={!isValid}
                                        className="ml-auto font-mono text-[10px] uppercase tracking-widest font-bold border border-fa-yellow text-fa-yellow hover:bg-fa-yellow hover:text-black px-3 py-1.5 disabled:opacity-40 flex items-center gap-1.5">
                                    <Lightning size={11} weight="fill" /> Challenge a friend (H2H)
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* LEADERBOARD */}
                <div className="border border-white/10 bg-[#0d0d0d]" data-testid="leaderboard">
                    <div className="px-3 py-2 border-b border-white/10 flex items-center gap-2">
                        <Trophy size={12} weight="fill" className="text-fa-yellow" />
                        <span className="font-mono text-[10px] uppercase tracking-widest text-fa-yellow">
                            Top simulations · {mode} mode
                        </span>
                    </div>
                    {leaderboard.length === 0 ? (
                        <div className="font-mono text-[11px] text-white/40 px-3 py-4">
                            No saved runs yet. Be the first — build, simulate, save.
                        </div>
                    ) : (
                        <table className="w-full">
                            <thead>
                                <tr className="font-mono text-[9px] uppercase tracking-widest text-white/40 border-b border-white/10">
                                    <th className="px-3 py-1.5 text-left">#</th>
                                    <th className="px-2 py-1.5 text-left">Squad</th>
                                    <th className="px-2 py-1.5 text-left">Manager</th>
                                    <th className="px-2 py-1.5 text-right">Avg</th>
                                    <th className="px-2 py-1.5 text-right">Ceiling</th>
                                </tr>
                            </thead>
                            <tbody>
                                {leaderboard.map((row, i) => (
                                    <tr key={row.run_id} className="border-b border-white/5 hover:bg-fa-yellow/[0.04] cursor-pointer transition-colors"
                                        data-testid={`lb-row-${i}`}
                                        onClick={() => window.open(`/lineup-sim/share/${row.run_id}`, "_blank", "noopener,noreferrer")}
                                        title="Open this squad in a new tab">
                                        <td className="px-3 py-1.5 font-display font-black text-fa-yellow tabular-nums">{i + 1}</td>
                                        <td className="px-2 py-1.5 font-mono text-[11px] underline decoration-dotted decoration-fa-yellow/40">{row.label}</td>
                                        <td className="px-2 py-1.5 font-mono text-[10px] text-white/50">{row.user_name || "Guest"}</td>
                                        <td className="px-2 py-1.5 font-mono text-[11px] tabular-nums text-fa-mint font-bold text-right">{row.total?.avg}p</td>
                                        <td className="px-2 py-1.5 font-mono text-[10px] tabular-nums text-fa-yellow text-right">{row.total?.ceiling}p</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </section>

            <WheelModal {...wheel}
                        teams={teams}
                        roster={roster}
                        filledIds={filledIds}
                        teamCounts={teamCounts}
                        budgetLeft={remaining}
                        maxPerClub={3}
                        difficulty={difficulty}
                        onPick={pickFromWheel}
                        onRespin={() => spinWheel(wheel.slot)}
                        onClose={() => setWheel({ open: false, slot: null, candidates: [], busy: false })} />

            <FreePickModal open={!!freePickSlot}
                           slot={freePickSlot}
                           roster={roster}
                           difficulty={difficulty}
                           filledIds={filledIds}
                           teamCounts={teamCounts}
                           budgetLeft={remaining}
                           maxPerClub={3}
                           onPick={pickFromFreePick}
                           onClose={() => setFreePickSlot(null)} />

            <SwapPickerModal open={swapPicker.open}
                             fromIdx={swapPicker.fromIdx}
                             allSlots={ALL_SLOTS}
                             squad={squad}
                             starterIdxs={starterIdxs}
                             onPick={handleSwapPick}
                             onClose={closeSwapPicker} />
        </AppShell>
    );
}
